import { useMemo, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import toast from "react-hot-toast";
import ProgressBar from "../components/getquote/ProgressBar";
import OptionCard from "../components/getquote/OptionCard";
import StepNav from "../components/getquote/StepNav";
import LoadingProgress from "../components/getquote/LoadingProgress";
import OTPModal from "../components/getquote/OTPModal";
import quoteService from "../services/quoteService";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";

const steps = [
  { id: "locations", percent: 13, title: "Locations" },
  { id: "move-type", percent: 25, title: "Move Type" },
  { id: "assistance", percent: 38, title: "Assistance Needed" },
  { id: "property", percent: 50, title: "Property Size" },
  { id: "project-details", percent: 63, title: "Project Details" },
  { id: "contact", percent: 75, title: "Contact Information" },
  { id: "review", percent: 100, title: "Review & Submit" },
];

const GetQuote = () => {
  const navigate = useNavigate();
  const { loginUser, user } = useAuth();
  const [stepIndex, setStepIndex] = useState(0);
  const [loadingPhase, setLoadingPhase] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [quoteId, setQuoteId] = useState(null);
  const [error, setError] = useState("");
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [form, setForm] = useState({
    // locations
    fromZip: "",
    fromCity: "",
    fromState: "",
    fromAddress: "",
    fromDate: "",
    fromTime: "",
    toZip: "",
    toCity: "",
    toState: "",
    toAddress: "",
    toDate: "",
    toTime: "",
    // selections
    moveCategory: "",
    moveType: "",
    assistance: "",
    property: "",
    // conditional details
    pianoType: "",
    pianoStairs: "",
    truckSize: "",
    vehicleType: "",
    vehicleMake: "",
    vehicleModel: "",
    vehicleYear: "",
    // project
    projectDesc: "",
    // contact
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
  });

  // Auto-fill user data if authenticated
  useEffect(() => {
    if (user) {
      const nameParts = user.name?.split(' ') || [];
      setForm(prev => ({
        ...prev,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        phone: user.phone || '',
        email: user.email || ''
      }));
      // User is already authenticated, mark OTP as verified
      setOtpVerified(true);
    }
  }, [user]);

  // ZIP code lookup states
  const [fromZipInfo, setFromZipInfo] = useState({
    city: "",
    state: "",
    error: "",
    loading: false,
  });
  const [toZipInfo, setToZipInfo] = useState({
    city: "",
    state: "",
    error: "",
    loading: false,
  });

  const current = steps[stepIndex];
  const canBack = stepIndex > 0 && !loadingPhase;

  const derivedPropertyTitle = (() => {
    if (current.id !== "property") return current.title;
    if (form.assistance === "Move a Piano") return "Piano Details";
    if (form.assistance === "Truck or Container Rental")
      return "Truck/Container Size";
    if (form.assistance === "Vehicle Move") return "Vehicle Details";
    return "Property Size";
  })();

  const next = async () => {
    if (loadingPhase) return;

    try {
      setError("");

      // Navigate through steps without saving until OTP verification
      // Only advance to next step, don't save to database yet

      // special 75% behavior -> show loading to 100%, then jump to contact (88%)
      if (current.id === "project-details") {
        setLoadingPhase(true);
        return;
      }

      // After contact, move to review step
      if (current.id === "contact") {
        // Just move to review step without OTP yet
        setStepIndex(steps.findIndex((s) => s.id === "review"));
        window.scrollTo(0, 0);
        return;
      }

      // On review step, handle OTP and save
      if (current.id === "review") {
        // If user is already authenticated, skip OTP verification
        if (user && otpVerified) {
          // User is authenticated, save quote and navigate directly
          await saveCompleteQuote();
          return;
        }

        // For non-authenticated users, proceed with OTP flow
        if (!otpSent) {
          // Check if phone or email already exists
          const { data: existingUsers, error: checkError } = await supabase
            .from('normal_user')
            .select('phone, email')
            .or(`phone.eq.${form.phone},email.eq.${form.email}`);

          if (checkError) {
            setError("Failed to validate user information. Please try again.");
            return;
          }

          if (existingUsers && existingUsers.length > 0) {
            const existingUser = existingUsers[0];
            let message = "Sorry, account already exists. Please sign in.";
            if (existingUser.phone === form.phone && existingUser.email === form.email) {
              message = "Sorry, account already exists. Please sign in.";
            } else if (existingUser.phone === form.phone) {
              message = "Sorry, this phone number is already registered. Please sign in.";
            } else if (existingUser.email === form.email) {
              message = "Sorry, this email address is already registered. Please sign in.";
            }
            setErrorPopupMessage(message);
            setShowErrorPopup(true);
            return;
          }

          // Send OTP to phone number
          const result = await quoteService.sendOTP(form.phone);
          if (result.success) {
            setOtpSent(true);
            setShowOTP(true);
          } else {
            setError(result.error || "Failed to send OTP");
          }
          return;
        } else if (!otpVerified) {
          // Wait for OTP verification
          setError("Please verify your phone number with the OTP code");
          return;
        } else if (otpVerified) {
          // OTP verified, save quote and navigate directly
          await saveCompleteQuote();
          return;
        }
      }

      // Default: just move to next step
      setStepIndex((i) => Math.min(i + 1, steps.length - 1));
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message || "An unexpected error occurred");
    }
  };

  // Function to save all quote data after OTP verification
  const saveCompleteQuote = async () => {
    try {
      setError("");

      // STEP 1: Create quote with location data
      const locationData = {
        origin_address: form.fromAddress,
        origin_city: form.fromCity,
        origin_state: form.fromState,
        origin_zip: form.fromZip,
        destination_address: form.toAddress,
        destination_city: form.toCity,
        destination_state: form.toState,
        destination_zip: form.toZip,
        move_date: form.fromDate,
      };

      const quoteResult = await quoteService.createQuote(locationData);
      if (!quoteResult.success) {
        setError(quoteResult.error || "Failed to save location data");
        return;
      }

      const newQuoteId = quoteResult.data.id;
      setQuoteId(newQuoteId);

      // STEP 2: Save move type and assistance
      const moveDetailsResult = await quoteService.saveMoveDetails(newQuoteId, {
        move_type: form.moveType,
        assistance_type: form.assistance,
      });
      if (!moveDetailsResult.success) {
        setError(moveDetailsResult.error || "Failed to save move details");
        return;
      }

      // STEP 3: Save property/conditional details
      let propertyResult;
      if (form.assistance === "Move a Piano") {
        propertyResult = await quoteService.savePianoMove(newQuoteId, {
          piano_type: form.pianoType,
          floors_stairs: parseInt(form.pianoStairs) || 0,
        });
      } else if (form.assistance === "Truck or Container Rental") {
        propertyResult = await quoteService.saveTruckContainerMove(newQuoteId, {
          truck_size: form.truckSize,
        });
      } else if (form.assistance === "Vehicle Move") {
        propertyResult = await quoteService.saveVehicleMove(newQuoteId, {
          vehicle_type: form.vehicleType,
          vehicle_make: form.vehicleMake,
          vehicle_model: form.vehicleModel,
          vehicle_year: parseInt(form.vehicleYear) || new Date().getFullYear(),
        });
      } else {
        propertyResult = await quoteService.savePropertySize(newQuoteId, {
          property_size: form.property,
        });
      }

      if (!propertyResult.success) {
        setError(propertyResult.error || "Failed to save property details");
        return;
      }

      // STEP 4: Save project details
      const projectResult = await quoteService.saveProjectDetails(newQuoteId, {
        project_description: form.projectDesc,
      });
      if (!projectResult.success) {
        setError(projectResult.error || "Failed to save project details");
        return;
      }

      // STEP 5: Link user to quote
      // If user is authenticated, link their existing user_id
      // Otherwise, create new user or update existing one
      if (user && user.id) {
        // User is authenticated, directly link to quote
        const { error: linkError } = await supabase
          .from('get_quote')
          .update({ user_id: user.id })
          .eq('id', newQuoteId)
          .select()
          .single();

        if (linkError) {
          setError("Failed to link quote to user");
          return;
        }
      } else {
        // User not authenticated, create/update user via service
        const userResult = await quoteService.saveUserAndLinkQuote(newQuoteId, {
          first_name: form.firstName,
          last_name: form.lastName,
          phone_number: form.phone,
          email: form.email,
        });
        if (!userResult.success) {
          setError(userResult.error || "Failed to save user information");
          return;
        }
      }

      // All data saved successfully — navigate straight to movers
      setQuoteId(newQuoteId);
      navigate(`/choose-movers?quoteId=${newQuoteId}`, {
        state: {
          form,
          quoteId: newQuoteId,
          originState: form.fromState,
          originZip: form.fromZip
        }
      });
    } catch (err) {
      setError(err.message || "Failed to save quote data");
    }
  };

  const back = () => {
    if (!canBack) return;
    setStepIndex((i) => Math.max(0, i - 1));
    window.scrollTo(0, 0);
  };

  const disableNext = useMemo(() => {
    switch (current.id) {
      case "locations":
        return (
          !form.fromZip ||
          !form.fromCity ||
          !form.fromAddress ||
          !form.toZip ||
          !form.toCity ||
          !form.toAddress ||
          !form.fromDate ||
          !smsConsent
        );
      case "move-type":
        return !form.moveType;
      case "assistance":
        return !form.assistance;
      case "property": {
        if (form.assistance === "Move a Piano") {
          return !form.pianoType || !form.pianoStairs;
        }
        if (form.assistance === "Truck or Container Rental") {
          return !form.truckSize;
        }
        if (form.assistance === "Vehicle Move") {
          return (
            !form.vehicleType ||
            !form.vehicleMake ||
            !form.vehicleModel ||
            !form.vehicleYear
          );
        }
        return !form.property;
      }
      case "project-details":
        return false; // Project description is optional
      case "contact":
        // If OTP verified, allow to continue
        if (otpVerified) return false;
        // Otherwise, must have basic info to send OTP
        return !form.firstName || !form.phone || !form.email;
      default:
        return false;
    }
  }, [current.id, form, otpVerified,smsConsent]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  // Function to fetch ZIP code data
  const lookupZipCode = async (zip, type) => {
    if (zip.length !== 5 || !/^\d{5}$/.test(zip)) {
      if (type === "from") {
        setFromZipInfo({ city: "", state: "", error: "", loading: false });
      } else {
        setToZipInfo({ city: "", state: "", error: "", loading: false });
      }
      return;
    }

    // Set loading state
    if (type === "from") {
      setFromZipInfo({ city: "", state: "", error: "", loading: true });
    } else {
      setToZipInfo({ city: "", state: "", error: "", loading: true });
    }

    try {
      const response = await fetch(`https://api.zippopotam.us/us/${zip}`);

      if (!response.ok) {
        throw new Error("Invalid ZIP code");
      }

      const data = await response.json();
      const city = data.places[0]["place name"];
      const state = data.places[0]["state abbreviation"];

      if (type === "from") {
        setFromZipInfo({ city, state, error: "", loading: false });
        setForm((f) => ({ ...f, fromCity: city, fromState: state }));
      } else {
        setToZipInfo({ city, state, error: "", loading: false });
        setForm((f) => ({ ...f, toCity: city, toState: state }));
      }
    } catch (error) {
      if (type === "from") {
        setFromZipInfo({
          city: "",
          state: "",
          error: "Invalid ZIP code",
          loading: false,
        });
      } else {
        setToZipInfo({
          city: "",
          state: "",
          error: "Invalid ZIP code",
          loading: false,
        });
      }
    }
  };

  // Watch for ZIP code changes
  useEffect(() => {
    if (form.fromZip.length === 5) {
      lookupZipCode(form.fromZip, "from");
    } else {
      setFromZipInfo({ city: "", state: "", error: "", loading: false });
    }
  }, [form.fromZip]);

  useEffect(() => {
    if (form.toZip.length === 5) {
      lookupZipCode(form.toZip, "to");
    } else {
      setToZipInfo({ city: "", state: "", error: "", loading: false });
    }
  }, [form.toZip]);

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-yellow-50/40 py-8 md:py-12">
      <Helmet>
        <title>Get a Free Moving Quote – MyMoveAdvisor</title>
        <meta name="description" content="Get free, no-obligation moving quotes from top-rated local movers in minutes. Compare prices and choose the best mover for your budget." />
        <meta name="keywords" content="free moving quote, moving estimate, compare movers, local moving companies" />
        <link rel="canonical" href="https://mymoveadvisor.com/quote" />
        <meta property="og:title" content="Get a Free Moving Quote – MyMoveAdvisor" />
        <meta property="og:description" content="Get free moving quotes in minutes. Compare and choose." />
        <meta property="og:url" content="https://mymoveadvisor.com/quote" />
      </Helmet>
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-yellow-200 shadow-[0_6px_24px_rgba(0,0,0,0.08)] p-5 md:p-7">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-orange-500 text-white rounded-xl w-11 h-11 flex items-center justify-center shadow">
              🚚
            </div>
          </div>
          <div className="text-center text-lg font-semibold text-gray-800">
            Get Your Free Moving Quote
          </div>
          {stepIndex === 0 && (
            <div className="flex justify-center mt-3">
              <Link to="/guide" className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full border border-green-100 text-[12px] font-bold text-green-700 shadow-sm transition-all hover:shadow-md hover:border-green-200 group">
                💡 Need help? View the Customer Guide
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          )}
          {!loadingPhase && (
            <div className="mt-2">
              <ProgressBar
                percent={current.percent}
                label={`${current.percent}% Complete • ${derivedPropertyTitle}`}
              />
            </div>
          )}

          {/* Loading interstitial after 75% */}
          {loadingPhase && (
            <LoadingProgress
              onDone={() => {
                setLoadingPhase(false);
                // jump to contact (88%) after loading finishes
                setStepIndex(steps.findIndex((s) => s.id === "contact"));
                window.scrollTo(0, 0);
              }}
            />
          )}

          {!loadingPhase && (
            <div className="mt-6">
              {current.id === "locations" && (
                <div>
                  <div className="font-semibold text-gray-800 text-lg mb-1">
                    Where are you moving from and to?
                  </div>
                  <div className="text-sm text-gray-500 mb-6">
                    Enter both origin and destination details
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    {/* Moving From */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-lg">
                          📦
                        </div>
                        <span className="font-semibold text-gray-800">
                          Moving From
                        </span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1.5">
                            ZIP Code
                          </label>
                          <input
                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                            placeholder="10001"
                            maxLength="5"
                            value={form.fromZip}
                            onChange={set("fromZip")}
                          />
                          {fromZipInfo.loading && (
                            <div className="mt-1.5 text-sm text-gray-500">
                              Looking up...
                            </div>
                          )}
                          {fromZipInfo.city && fromZipInfo.state && (
                            <div className="mt-1.5 text-sm font-medium text-orange-600">
                              {fromZipInfo.city}, {fromZipInfo.state}
                            </div>
                          )}
                          {fromZipInfo.error && (
                            <div className="mt-1.5 text-sm font-medium text-red-600">
                              {fromZipInfo.error}
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1.5">
                            City
                          </label>
                          <input
                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                            placeholder="Newyork"
                            value={form.fromCity}
                            onChange={set("fromCity")}
                            readOnly={!!fromZipInfo.city}
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1.5">
                            Street Address
                          </label>
                          <input
                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                            placeholder="742 Evergreen Terrace
Springfield, IL 62704"
                            value={form.fromAddress}
                            onChange={set("fromAddress")}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Moving To */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-lg">
                          📍
                        </div>
                        <span className="font-semibold text-gray-800">
                          Moving To
                        </span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1.5">
                            ZIP Code
                          </label>
                          <input
                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                            placeholder="90210"
                            maxLength="5"
                            value={form.toZip}
                            onChange={set("toZip")}
                          />
                          {toZipInfo.loading && (
                            <div className="mt-1.5 text-sm text-gray-500">
                              Looking up...
                            </div>
                          )}
                          {toZipInfo.city && toZipInfo.state && (
                            <div className="mt-1.5 text-sm font-medium text-orange-600">
                              {toZipInfo.city}, {toZipInfo.state}
                            </div>
                          )}
                          {toZipInfo.error && (
                            <div className="mt-1.5 text-sm font-medium text-red-600">
                              {toZipInfo.error}
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1.5">
                            City
                          </label>
                          <input
                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                            placeholder="Beverly Hills"
                            value={form.toCity}
                            onChange={set("toCity")}
                            readOnly={!!toZipInfo.city}
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1.5">
                            Street Address
                          </label>
                          <input
                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                            placeholder="742 Evergreen Terrace
Springfield, IL 62704"
                            value={form.toAddress}
                            onChange={set("toAddress")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <label className="block text-sm text-gray-600 mb-1.5 text-center">Moving Date</label>
                    <div className="flex justify-center">
                      <input
                        type="date"
                        className="w-full md:w-80 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                        value={form.fromDate}
                        onChange={set("fromDate")}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  {/* SMS Consent Checkbox */}
                  <div className="mt-6 flex items-start gap-3 py-2 px-4 bg-orange-50 rounded-lg border border-orange-100">
                    <input
                      type="checkbox"
                      id="smsConsent"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      className="mt-1 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                    />
                    <label htmlFor="smsConsent" className="text-sm text-gray-600 leading-tight">
                      By providing your mobile number, you agree to receive transactional <strong>SMS</strong> messages from My Move Advisor including verification codes and account-related notifications. Message frequency varies. Message &amp; data rates may apply. Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for assistance.
                    </label>
                  </div>
                </div>
              )}

              {current.id === "move-type" && (
                <div>
                  <div className="font-semibold text-gray-800 mb-2">
                    What type of move is this?
                  </div>
                  <div className="text-sm text-gray-500 mb-4">
                    Choose the service that fits your needs
                  </div>
                  <div className="space-y-3">
                    <OptionCard
                      icon="🚚"
                      title="Local Move"
                      subtitle="Within the same city"
                      selected={form.moveType === "Local"}
                      onClick={() => set("moveType")("Local")}
                    />
                    <OptionCard
                      icon="🛣️"
                      title="In State Move"
                      subtitle="Within the same state"
                      selected={form.moveType === "In State"}
                      onClick={() => set("moveType")("In State")}
                    />
                    <OptionCard
                      icon="🗺️"
                      title="Out of State Move"
                      subtitle="Moving to another state"
                      selected={form.moveType === "Out of State"}
                      onClick={() => set("moveType")("Out of State")}
                    />
                  </div>
                </div>
              )}

              {current.id === "assistance" && (
                <div>
                  <div className="font-semibold text-gray-800 mb-2">
                    What kind of assistance do you need?
                  </div>
                  <div className="text-sm text-gray-500 mb-4">
                    Select the type of help you require
                  </div>
                  <div className="space-y-3">
                    <OptionCard
                      icon="📦"
                      title="Packaging and Boxing"
                      selected={form.assistance === "Packaging and Boxing"}
                      onClick={() => set("assistance")("Packaging and Boxing")}
                    />
                    <OptionCard
                      icon="🚛"
                      title="Just Moving"
                      selected={form.assistance === "Just Moving"}
                      onClick={() => set("assistance")("Just Moving")}
                    />
                    <OptionCard
                      icon="🧳"
                      title="Just Packing"
                      selected={form.assistance === "Just Packing"}
                      onClick={() => set("assistance")("Just Packing")}
                    />
                    <OptionCard
                      icon="🎹"
                      title="Move a Piano"
                      selected={form.assistance === "Move a Piano"}
                      onClick={() => set("assistance")("Move a Piano")}
                    />
                    <OptionCard
                      icon="🚚"
                      title="Truck or Container Rental"
                      selected={form.assistance === "Truck or Container Rental"}
                      onClick={() =>
                        set("assistance")("Truck or Container Rental")
                      }
                    />
                    <OptionCard
                      icon="🚗"
                      title="Vehicle Move"
                      selected={form.assistance === "Vehicle Move"}
                      onClick={() => set("assistance")("Vehicle Move")}
                    />
                  </div>
                </div>
              )}

              {current.id === "property" && (
                <div>
                  {form.assistance === "Move a Piano" && (
                    <div>
                      <div className="font-semibold text-gray-800 mb-2">
                        What type of piano are you moving?
                      </div>
                      <div className="text-sm text-gray-500 mb-4">
                        Select your piano type and stairs/floors
                      </div>
                      <div className="space-y-3 mb-4">
                        <OptionCard
                          title="Upright Piano"
                          subtitle="Standard upright piano"
                          selected={form.pianoType === "Upright Piano"}
                          onClick={() => set("pianoType")("Upright Piano")}
                        />
                        <OptionCard
                          title="Baby Grand"
                          subtitle="Small grand piano"
                          selected={form.pianoType === "Baby Grand"}
                          onClick={() => set("pianoType")("Baby Grand")}
                        />
                        <OptionCard
                          title="Grand Piano"
                          subtitle="Full-size grand piano"
                          selected={form.pianoType === "Grand Piano"}
                          onClick={() => set("pianoType")("Grand Piano")}
                        />
                        <OptionCard
                          title="Digital Piano"
                          subtitle="Electronic keyboard/piano"
                          selected={form.pianoType === "Digital Piano"}
                          onClick={() => set("pianoType")("Digital Piano")}
                        />
                      </div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Number of floors/stairs involved
                      </label>
                      <input
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                        placeholder="e.g., 2"
                        value={form.pianoStairs}
                        onChange={set("pianoStairs")}
                      />
                    </div>
                  )}

                  {form.assistance === "Truck or Container Rental" && (
                    <div>
                      <div className="font-semibold text-gray-800 mb-2">
                        What size truck or container do you need?
                      </div>
                      <div className="text-sm text-gray-500 mb-4">
                        Select the size that fits your move
                      </div>
                      <div className="space-y-3">
                        <OptionCard
                          title="10 ft Truck"
                          subtitle="Small moves, 1-2 rooms"
                          selected={form.truckSize === "10 ft Truck"}
                          onClick={() => set("truckSize")("10 ft Truck")}
                        />
                        <OptionCard
                          title="15 ft Truck"
                          subtitle="Studio to 1 bedroom"
                          selected={form.truckSize === "15 ft Truck"}
                          onClick={() => set("truckSize")("15 ft Truck")}
                        />
                        <OptionCard
                          title="20 ft Truck"
                          subtitle="2-3 bedrooms"
                          selected={form.truckSize === "20 ft Truck"}
                          onClick={() => set("truckSize")("20 ft Truck")}
                        />
                        <OptionCard
                          title="26 ft Truck"
                          subtitle="3-4 bedrooms"
                          selected={form.truckSize === "26 ft Truck"}
                          onClick={() => set("truckSize")("26 ft Truck")}
                        />
                        <OptionCard
                          title="Small Container"
                          subtitle="7-8 ft container"
                          selected={form.truckSize === "Small Container"}
                          onClick={() => set("truckSize")("Small Container")}
                        />
                        <OptionCard
                          title="Large Container"
                          subtitle="16-20 ft container"
                          selected={form.truckSize === "Large Container"}
                          onClick={() => set("truckSize")("Large Container")}
                        />
                      </div>
                    </div>
                  )}

                  {form.assistance === "Vehicle Move" && (
                    <div>
                      <div className="font-semibold text-gray-800 mb-2">
                        What type of vehicle are you moving?
                      </div>
                      <div className="text-sm text-gray-500 mb-4">
                        Select your vehicle type and provide details
                      </div>
                      <div className="space-y-3 mb-4">
                        <OptionCard
                          title="Car"
                          subtitle="Sedan, coupe, or hatchback"
                          selected={form.vehicleType === "Car"}
                          onClick={() => set("vehicleType")("Car")}
                        />
                        <OptionCard
                          title="SUV/Truck"
                          subtitle="SUV, pickup truck, or van"
                          selected={form.vehicleType === "SUV/Truck"}
                          onClick={() => set("vehicleType")("SUV/Truck")}
                        />
                        <OptionCard
                          title="Motorcycle"
                          subtitle="Motorcycle or scooter"
                          selected={form.vehicleType === "Motorcycle"}
                          onClick={() => set("vehicleType")("Motorcycle")}
                        />
                        <OptionCard
                          title="Boat/RV"
                          subtitle="Boat, RV, or trailer"
                          selected={form.vehicleType === "Boat/RV"}
                          onClick={() => set("vehicleType")("Boat/RV")}
                        />
                        <OptionCard
                          title="Classic/Luxury"
                          subtitle="Classic or luxury vehicle"
                          selected={form.vehicleType === "Classic/Luxury"}
                          onClick={() => set("vehicleType")("Classic/Luxury")}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">
                            Make
                          </label>
                          <input
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                            placeholder="Toyota"
                            value={form.vehicleMake}
                            onChange={set("vehicleMake")}
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">
                            Model
                          </label>
                          <input
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                            placeholder="Camry"
                            value={form.vehicleModel}
                            onChange={set("vehicleModel")}
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">
                            Year
                          </label>
                          <input
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                            placeholder="2020"
                            value={form.vehicleYear}
                            onChange={set("vehicleYear")}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {(!form.assistance ||
                    (form.assistance !== "Move a Piano" &&
                      form.assistance !== "Truck or Container Rental" &&
                      form.assistance !== "Vehicle Move")) && (
                      <div>
                        <div className="font-semibold text-gray-800 mb-2">
                          How large is your property?
                        </div>
                        <div className="text-sm text-gray-500 mb-4">
                          Select the size that best describes your space
                        </div>
                        <div className="space-y-3">
                          <OptionCard
                            title="Studio"
                            subtitle="Small apartment"
                            selected={form.property === "Studio"}
                            onClick={() => set("property")("Studio")}
                          />
                          <OptionCard
                            title="1 Bedroom"
                            subtitle="One bedroom apartment/house"
                            selected={form.property === "1 Bedroom"}
                            onClick={() => set("property")("1 Bedroom")}
                          />
                          <OptionCard
                            title="2-3 Bedrooms"
                            subtitle="Medium-sized home"
                            selected={form.property === "2-3 Bedrooms"}
                            onClick={() => set("property")("2-3 Bedrooms")}
                          />
                          <OptionCard
                            title="4+ Bedrooms"
                            subtitle="Large home"
                            selected={form.property === "4+ Bedrooms"}
                            onClick={() => set("property")("4+ Bedrooms")}
                          />
                          <OptionCard
                            title="Mansion"
                            subtitle="Estate or very large property"
                            selected={form.property === "Mansion"}
                            onClick={() => set("property")("Mansion")}
                          />
                        </div>
                      </div>
                    )}
                </div>
              )}

              {current.id === "project-details" && (
                <div>
                  <div className="font-semibold text-gray-800 mb-2">
                    Tell us more about your project
                  </div>
                  <div className="text-sm text-gray-500 mb-4">
                    Provide details that help movers give accurate quotes
                  </div>
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Project Description (Optional)
                    </label>
                    <textarea
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 h-28 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                      placeholder="Describe your moving project, special items, access restrictions, timing requirements, preferred moving date…"
                      value={form.projectDesc}
                      onChange={set("projectDesc")}
                    />
                  </div>
                </div>
              )}

              {current.id === "contact" && (
                <div>
                  <div className="font-semibold text-gray-800 mb-2">
                    We have matching professional movers in your area
                  </div>
                  <div className="text-sm text-gray-500 mb-4">
                    Fill your info to receive quotes
                  </div>
                  {user && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                      ✓ Using your account information
                    </div>
                  )}

                  {/* Step 1: Name fields — always visible */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        value={form.firstName}
                        onChange={set("firstName")}
                        placeholder="John"
                        disabled={!!user}
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        value={form.lastName}
                        onChange={set("lastName")}
                        placeholder="Doe"
                        disabled={!!user}
                      />
                    </div>
                  </div>

                  {/* Step 2: Phone & email — reveal once both names are filled */}
                  <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                      form.firstName.trim() && form.lastName.trim()
                        ? "max-h-[500px] opacity-100 mt-4"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    {!user && form.firstName.trim() && form.lastName.trim() && (
                      <div className="mb-3 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Hi <strong>{form.firstName}</strong>Now enter your contact details below.
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <div className="flex rounded-lg border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-orange-300 focus-within:border-orange-500">
                          <span className="flex items-center px-3 bg-gray-100 text-gray-600 text-sm font-semibold border-r border-gray-300 select-none">+1</span>
                          <input
                            className="flex-1 px-3 py-2 text-sm focus:outline-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                            value={form.phone.replace(/^\+1/, '')}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                              set("phone")(`+1${digits}`);
                            }}
                            placeholder="555 123 4567"
                            maxLength={10}
                            disabled={otpSent || !!user}
                          />
                        </div>
                        {otpSent && !otpVerified && (
                          <div className="mt-2 text-sm text-green-600">
                            OTP sent to {form.phone}
                          </div>
                        )}
                        {otpVerified && (
                          <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
                            ✓ {user ? 'Account verified' : 'Phone number verified'}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          Email Address
                        </label>
                        <input
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          type="email"
                          value={form.email}
                          onChange={set("email")}
                          placeholder="john@example.com"
                          disabled={!!user}
                        />
                      </div>
                    </div>
                    <div className="mt-4 text-xs text-gray-500 leading-relaxed bg-gray-50 p-3 rounded-lg border border-gray-200">
                      By providing your mobile number, you agree to receive transactional <strong>SMS</strong> messages from My Move Advisor including verification codes and account-related notifications. Message frequency varies. Message &amp; data rates may apply. Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for assistance.
                    </div>
                  </div>

                  {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                      {error}
                    </div>
                  )}
                </div>
              )}

              {current.id === "review" && (
                <div>
                  <div className="font-semibold text-gray-800 mb-2">
                    Review your details
                  </div>
                  <div className="text-sm text-gray-500 mb-6">
                    Please verify all information is correct before submitting
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 shadow-[0_6px_24px_rgba(0,0,0,0.06)] overflow-hidden">
                    <div className="bg-gray-50 px-5 py-3 border-b flex items-center justify-between">
                      <div className="font-semibold text-gray-800">
                        Quote Receipt
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date().toLocaleString()}
                      </div>
                    </div>
                    <div className="p-5 space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="rounded-lg border p-4">
                          <div className="font-semibold text-gray-700 mb-2">
                            Move Summary
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <div>
                              <span className="font-medium text-gray-800">
                                Category:
                              </span>{" "}
                              {form.moveCategory || "—"}
                            </div>
                            <div>
                              <span className="font-medium text-gray-800">
                                Move Type:
                              </span>{" "}
                              {form.moveType || "—"}
                            </div>
                            <div>
                              <span className="font-medium text-gray-800">
                                Assistance:
                              </span>{" "}
                              {form.assistance || "—"}
                            </div>
                            <div>
                              <span className="font-medium text-gray-800">
                                Property:
                              </span>{" "}
                              {form.property || "—"}
                            </div>
                          </div>
                        </div>
                        <div className="rounded-lg border p-4">
                          <div className="font-semibold text-gray-700 mb-2">
                            Contact
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <div>
                              <span className="font-medium text-gray-800">
                                Name:
                              </span>{" "}
                              {[form.firstName, form.lastName]
                                .filter(Boolean)
                                .join(" ") || "—"}
                            </div>
                            <div>
                              <span className="font-medium text-gray-800">
                                Phone:
                              </span>{" "}
                              {form.phone || "—"}
                            </div>
                            <div>
                              <span className="font-medium text-gray-800">
                                Email:
                              </span>{" "}
                              {form.email || "—"}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border p-4">
                        <div className="font-semibold text-gray-700 mb-3">
                          Locations
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className="text-sm font-medium text-gray-700 mb-1">
                              🏁 Moving From
                            </div>
                            <div className="text-sm text-gray-600">
                              <div>{form.fromAddress || "—"}</div>
                              <div>
                                {[form.fromCity, form.fromState, form.fromZip]
                                  .filter(Boolean)
                                  .join(", ") || "—"}
                              </div>
                              <div className="mt-1 text-sm text-gray-500">
                                <span className="font-medium text-gray-700">Moving date: </span>{form.fromDate || "—"}
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-700 mb-1">
                              📍 Moving To
                            </div>
                            <div className="text-sm text-gray-600">
                              <div>{form.toAddress || "—"}</div>
                              <div>
                                {[form.toCity, form.toState, form.toZip]
                                  .filter(Boolean)
                                  .join(", ") || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        {form.assistance === "Move a Piano" && (
                          <div className="mt-4 text-sm text-gray-600 space-y-1">
                            <div>
                              <span className="font-medium text-gray-800">
                                Piano Type:
                              </span>{" "}
                              {form.pianoType || "—"}
                            </div>
                            <div>
                              <span className="font-medium text-gray-800">
                                Floors/Stairs:
                              </span>{" "}
                              {form.pianoStairs || "—"}
                            </div>
                          </div>
                        )}
                        {form.assistance === "Truck or Container Rental" && (
                          <div className="mt-4 text-sm text-gray-600">
                            <span className="font-medium text-gray-800">
                              Truck/Container Size:
                            </span>{" "}
                            {form.truckSize || "—"}
                          </div>
                        )}
                        {form.assistance === "Vehicle Move" && (
                          <div className="mt-4 text-sm text-gray-600 space-y-1">
                            <div>
                              <span className="font-medium text-gray-800">
                                Vehicle Type:
                              </span>{" "}
                              {form.vehicleType || "—"}
                            </div>
                            <div>
                              <span className="font-medium text-gray-800">
                                Make/Model/Year:
                              </span>{" "}
                              {[
                                form.vehicleMake,
                                form.vehicleModel,
                                form.vehicleYear,
                              ]
                                .filter(Boolean)
                                .join(" ") || "—"}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="rounded-lg border p-4">
                        <div className="font-semibold text-gray-700 mb-2">
                          Project Description
                        </div>
                        <div className="text-sm text-gray-600 whitespace-pre-wrap">
                          {form.projectDesc || "—"}
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 text-center bg-blue-50 border border-blue-200 rounded-lg p-3">
                        {user ? (
                          <>
                            <svg className="w-5 h-5 text-green-600 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <strong className="text-green-900">Signed in as {user.name}</strong>
                            <br />
                            Click "Submit &amp; View Movers" to submit and go directly to your matches.
                          </>
                        ) : otpVerified ? (
                          <>
                            <svg className="w-5 h-5 text-green-600 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <strong className="text-green-900">Phone number verified!</strong>
                            <br />
                            Click "Submit &amp; View Movers" to submit and go directly to your matches.
                          </>
                        ) : otpSent ? (
                          <>
                            <svg className="w-5 h-5 text-orange-600 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            <strong className="text-orange-900">OTP sent to {form.phone}</strong>
                            <br />
                            Please enter the verification code to continue.
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 text-blue-600 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <strong className="text-blue-900">Almost there! Review your details carefully.</strong>
                            <br />
                            Go back to edit anything, then click "Send OTP" to verify your phone and submit.
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <StepNav
                onBack={back}
                onNext={next}
                nextLabel={
                  current.id === "review"
                    ? (user
                      ? "Submit & View Movers"
                      : (otpVerified ? "Submit & View Movers" : "Send OTP")
                    )
                    : "Next"
                }
                disableNext={disableNext}
              />
            </div>
          )}
          <OTPModal
            isOpen={showOTP}
            phone={form.phone}
            onClose={() => {
              setShowOTP(false);
              setOtpSent(false);
            }}
            onVerify={async (code) => {
              try {
                const result = await quoteService.verifyOTP(form.phone, code);
                if (result.success) {
                  setOtpVerified(true);
                  setShowOTP(false);
                  setError("");

                  // Save all the quote data first (this creates the user in the database)
                  await saveCompleteQuote();

                  // Now log in the user (user exists in database now)
                  try {
                    await loginUser(form.phone);
                  } catch (loginError) {
                    // Continue anyway - user data was saved
                  }
                } else {
                  setError(result.error || "Invalid OTP code. Please try again.");
                }
              } catch (err) {
                setError("Failed to verify OTP. Please try again.");
              }
            }}
          />

          {/* Error Popup Modal */}
          {showErrorPopup && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Account Already Exists
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  {errorPopupMessage}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowErrorPopup(false);
                      navigate('/signin-user');
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => setShowErrorPopup(false)}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GetQuote;
